import React, { useEffect, useState } from "react";
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoHelper from "global/components/InfoHelperText/InfoHelper";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { getRenewableGraphData } from "screens/RenewableDocuments/RenewableFunction";
import { RENEWABLE_CONSTANTS } from "screens/RenewableDocuments/RenewableConstants";
import { useFeatureState, useFeatures } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import strings from "global/constants/StringConstants";
import { focusRing } from "theme/colors";
import { dashFont, dashRef } from "./DashboardReferenceStyles";

const STANDALONE_CHART_MIN = 200;
// The phone reference draws a shorter plot (126px of bars plus its labels).
const STANDALONE_CHART_MIN_PHONE = 168;

// Off-screen but still in the accessibility tree — used to expose the chart's
// data as a real table for screen-reader / keyboard users while the SVG itself
// is hidden from assistive tech.
const visuallyHidden = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
};

type Props = {
  /** When true, chart grows to fill the card beside Recent activity (flex layout). */
  fillAvailableHeight?: boolean;
};

const DashboardRenewableChart = ({ fillAvailableHeight = false }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Tablet keeps the full "Jul 2026" tick; the phone reference shortens it to
  // the month alone and drops the plot height to suit.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const monthOptions = RENEWABLE_CONSTANTS.MONTH_OPTIONS;

  const { loaded: featuresLoaded } = useFeatures();
  const renewalState = useFeatureState(
    FeatureKeys.ENVELOPE_RENEWAL_NOTIFICATIONS,
  );
  const renewalAvailable = renewalState.status === "available";

  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<number>(6);

  useEffect(() => {
    if (!featuresLoaded) return;
    if (!renewalAvailable) {
      setIsLoading(false);
      return;
    }
    getRenewableGraphData(selectedMonths, setIsLoading, setChartData);
  }, [selectedMonths, renewalAvailable, featuresLoaded]);

  const handleBarClick = (data: any) => {
    // Zero months render as stubs, so guard against navigating to an empty list.
    if (data?.originalDate && data?.count > 0) {
      history.push({
        pathname: viewpaths.renewableDocumentsListViewPath,
        state: { date: data.originalDate },
      });
    }
  };

  // Recharts forwards `tabIndex`/`role`/`aria-label`/`id` from a Cell onto the
  // bar's <path>, but not event handlers, so activation is caught as the
  // keydown bubbles out of the focused bar and the index read back from its id.
  const BAR_ID_PREFIX = "renewal-bar-";

  const handleBarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const barId = (event.target as HTMLElement)?.id ?? "";
    if (!barId.startsWith(BAR_ID_PREFIX)) return;
    const entry = chartData[Number(barId.slice(BAR_ID_PREFIX.length))];
    if (!entry?.originalDate || !(entry?.count > 0)) return;
    event.preventDefault();
    handleBarClick(entry);
  };

  const rootStackSx = fillAvailableHeight
    ? {
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column" as const,
      }
    : { minHeight: 0 };

  // The plot sits directly on the dashboard card — no inner panel of its own,
  // which would read as a second box inside the section card.
  const chartPaperSx = fillAvailableHeight
    ? {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column" as const,
        background: "transparent",
      }
    : { background: "transparent" };

  const chartPlotBoxSx = fillAvailableHeight
    ? {
        flex: 1,
        minHeight: 160,
        width: "100%",
        position: "relative" as const,
        "& .recharts-wrapper": { outline: "none !important" },
        "& .recharts-surface": { outline: "none !important" },
        "& :focus:not(:focus-visible)": { outline: "none !important" },
        "& .renewal-bar:focus-visible": {
          outline: `2px solid ${focusRing}`,
          outlineOffset: "2px",
        },
      }
    : {
        width: "100%",
        minHeight: { xs: STANDALONE_CHART_MIN_PHONE, sm: STANDALONE_CHART_MIN },
        height: { xs: STANDALONE_CHART_MIN_PHONE, sm: STANDALONE_CHART_MIN },
        position: "relative" as const,
        "& .recharts-wrapper": { outline: "none !important" },
        "& .recharts-surface": { outline: "none !important" },
        "& :focus:not(:focus-visible)": { outline: "none !important" },
        "& .renewal-bar:focus-visible": {
          outline: `2px solid ${focusRing}`,
          outlineOffset: "2px",
        },
      };

  // Bordered pill with a small chevron, per the reference.
  const monthFilter = (
    <Select
      id="dashboard-renewable-month-filter"
      value={selectedMonths}
      onChange={(event) => setSelectedMonths(Number(event.target.value))}
      disabled={isLoading}
      IconComponent={KeyboardArrowDownIcon}
      inputProps={{ "aria-label": "Renewal range in months" }}
      MenuProps={{ PaperProps: { sx: { borderRadius: "10px", mt: 0.5 } } }}
      sx={{
        flexShrink: 0,
        borderRadius: "9px",
        fontSize: dashFont.filter,
        fontWeight: 700,
        color: dashRef.filterText,
        "& .MuiSelect-select": {
          padding: { xs: "6px 9px !important", sm: "8px 12px !important" },
          paddingRight: { xs: "28px !important", sm: "32px !important" },
        },
        "& .MuiSelect-select:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: dashRef.filterBorder,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: dashRef.filterBorderHover,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: dashRef.filterBorderHover,
          borderWidth: "1px",
        },
        "& .MuiSelect-icon": {
          fontSize: 16,
          right: 10,
          color: dashRef.filterText,
        },
      }}
    >
      {monthOptions.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          sx={{ fontSize: dashFont.filter, fontWeight: 600 }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );

  const descriptionSx = {
    fontSize: dashFont.cardDescription,
    color: dashRef.metaText,
    lineHeight: 1.45,
  };

  // Title and range picker share a row; the description sits beneath the title.
  const renderHeader = (withFilter: boolean) => (
    <Stack sx={{ flexShrink: 0 }}>
      <Stack
        // The range picker keeps the title's row at every width — it shrinks
        // rather than dropping below it on the phone.
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        {/* From `sm` up the description is stacked under the title, not under
            the whole row, so the taller range picker can't widen the
            title-to-description gap. */}
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              component="h2"
              sx={{
                fontSize: dashFont.cardHeading,
                fontWeight: 800,
                color: dashRef.heading,
              }}
            >
              {strings.DASHBOARD_MONTHLY_RENEWALS_TITLE}
            </Typography>
            {!withFilter && (
              <InfoHelper
                title={strings.DASHBOARD_MONTHLY_RENEWALS_TITLE}
                description={strings.DASHBOARD_MONTHLY_RENEWALS_INFO_SHORT}
              />
            )}
          </Stack>
          <Typography
            sx={{ ...descriptionSx, display: { xs: "none", sm: "block" } }}
          >
            {strings.DASHBOARD_MONTHLY_RENEWALS_SUBTITLE}
          </Typography>
        </Stack>
        {withFilter && monthFilter}
      </Stack>
      {/* On the phone the title column is only ~230px wide once the range
          picker takes its share, which wraps this line in two. The reference
          runs it under the whole row instead. */}
      <Typography
        sx={{
          ...descriptionSx,
          mt: 0.5,
          display: { xs: "block", sm: "none" },
        }}
      >
        {strings.DASHBOARD_MONTHLY_RENEWALS_SUBTITLE}
      </Typography>
    </Stack>
  );

  const renderGatedHeader = () => renderHeader(false);

  // Text alternative for the bar chart (WCAG 1.1.1). The SVG itself is hidden
  // from assistive tech; this off-screen table carries the same data as an
  // accessible, structured equivalent.
  const renderChartDataTable = () => (
    <Box component="table" sx={visuallyHidden}>
      <caption>{strings.DASHBOARD_MONTHLY_RENEWALS_TITLE}</caption>
      <thead>
        <tr>
          <th scope="col">Month</th>
          <th scope="col">Renewals due</th>
        </tr>
      </thead>
      <tbody>
        {chartData.length > 0 ? (
          chartData.map((entry, index) => (
            <tr key={`renewal-row-${index}`}>
              <th scope="row">{String(entry?.displayDate ?? "").trim()}</th>
              <td>{Number(entry?.count ?? 0)}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={2}>No renewal data available.</td>
          </tr>
        )}
      </tbody>
    </Box>
  );

  // Two-line month/year tick, as in the reference.
  const renderMonthTick = ({ x, y, payload }: any) => {
    const value = String(payload?.value ?? "");
    const [month, year] = value.split(" ");
    // Three ticks, one per reference column: the phone shows the month alone
    // in caps, the full-width tablet card fits "Jul 2026" on one line, and the
    // desktop card — which is only half the row wide — keeps the year stacked.
    if (isPhone) {
      return (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
          fontSize={9.5}
          fontWeight={700}
        >
          <tspan x={x} dy="1em">
            {String(month ?? "").toUpperCase()}
          </tspan>
        </text>
      );
    }
    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        fill={theme.palette.text.secondary}
        fontSize={11}
      >
        <tspan x={x} dy="1em">
          {isMobile ? value : month}
        </tspan>
        {year && !isMobile && (
          <tspan x={x} dy="1.15em">
            {year}
          </tspan>
        )}
      </text>
    );
  };

  // Count above each bar — muted for months with nothing due.
  const renderBarValueLabel = ({ x, y, width, value }: any) => {
    const count = Number(value ?? 0);
    return (
      <text
        x={x + width / 2}
        y={y - 7}
        textAnchor="middle"
        fontSize={isPhone ? 10.5 : 12}
        fontWeight={700}
        fill={count > 0 ? theme.palette.text.primary : theme.palette.text.disabled}
      >
        {count}
      </text>
    );
  };

  if (renewalState.status === "not-in-plan") {
    return (
      <Stack spacing={1.25} sx={rootStackSx}>
        {renderGatedHeader()}
        <Paper
          elevation={0}
          sx={{ ...chartPaperSx, flex: fillAvailableHeight ? 1 : undefined }}
        >
          <FeatureGateBlock status="not-in-plan" variant="inline" />
        </Paper>
      </Stack>
    );
  }

  if (renewalState.status === "admin-disabled") {
    return (
      <Stack spacing={1.25} sx={rootStackSx}>
        {renderGatedHeader()}
        <Paper
          elevation={0}
          sx={{ ...chartPaperSx, flex: fillAvailableHeight ? 1 : undefined }}
        >
          <FeatureGateBlock
            status="admin-disabled"
            variant="inline"
            disabledMessage={renewalState.disabledMessage}
          />
        </Paper>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack spacing={1.25} sx={rootStackSx}>
        {/* Header stays put while the range reloads; only the plot is a
            skeleton, and the picker is disabled until data arrives. */}
        {renderHeader(true)}
        <Paper
          elevation={0}
          aria-busy="true"
          sx={{ ...chartPaperSx, flex: fillAvailableHeight ? 1 : undefined }}
        >
          <Skeleton
            variant="rounded"
            width="100%"
            height="100%"
            sx={{
              borderRadius: 1,
              minHeight: fillAvailableHeight
                ? 160
                : {
                    xs: STANDALONE_CHART_MIN_PHONE,
                    sm: STANDALONE_CHART_MIN,
                  },
            }}
          />
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25} sx={rootStackSx}>
      {renderHeader(true)}
      <Paper elevation={0} sx={chartPaperSx}>
        {renderChartDataTable()}
        <Box sx={chartPlotBoxSx} onKeyDown={handleBarKeyDown}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              accessibilityLayer={false}
              tabIndex={-1}
              data={chartData}
              margin={{
                top: 24,
                right: 4,
                left: 4,
                bottom: 0,
              }}
            >
              <defs>
                <linearGradient
                  id="renewalBarGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#DD3824" stopOpacity={1} />
                  <stop offset="100%" stopColor="#FF3E00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="displayDate"
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={false}
                interval={0}
                // One line of ticks below `md`, two above it.
                height={isPhone ? 24 : isMobile ? 26 : 42}
                tick={renderMonthTick}
              />
              <Tooltip
                cursor={{ fill: theme.palette.action.hover }}
                contentStyle={{
                  borderRadius: 8,
                  border: "none",
                  boxShadow: theme.shadows[4],
                }}
              />
              <Bar
                dataKey="count"
                radius={[4, 4, 0, 0]}
                maxBarSize={isPhone ? 28 : isMobile ? 38 : 44}
                // Gives months with nothing due a visible stub instead of
                // dropping them out of the plot entirely.
                minPointSize={6}
                onClick={handleBarClick}
              >
                {chartData.map((entry, index) => {
                  const isInteractive = entry?.count > 0;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      id={`${BAR_ID_PREFIX}${index}`}
                      className={isInteractive ? "renewal-bar" : undefined}
                      tabIndex={isInteractive ? 0 : -1}
                      role={isInteractive ? "button" : undefined}
                      aria-label={
                        isInteractive
                          ? `${String(entry?.displayDate ?? "").trim()}, ${entry.count} renewals due. Open the list for this month.`
                          : undefined
                      }
                      fill={
                        isInteractive
                          ? "url(#renewalBarGradient)"
                          : theme.palette.action.disabledBackground
                      }
                      style={{
                        cursor: isInteractive ? "pointer" : "default",
                      }}
                    />
                  );
                })}
                <LabelList dataKey="count" content={renderBarValueLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Stack>
  );
};

export default DashboardRenewableChart;
